import React, { useState, useRef, useEffect, KeyboardEvent } from 'react';

interface InlineEditProps {
  value: string;
  onSave: (newValue: string) => void;
  placeholder?: string;
  className?: string;
  displayClassName?: string;
  editClassName?: string;
  multiline?: boolean;
  maxLength?: number;
  disabled?: boolean;
  validateLength?: (text: string) => boolean; // 自定义长度验证函数
  lengthMessage?: string; // 长度超限时的提示信息
  getDisplayLength?: (text: string) => number; // 获取显示长度的函数
  maxDisplayLength?: number; // 最大显示长度
}

export const InlineEdit: React.FC<InlineEditProps> = ({
  value,
  onSave,
  placeholder = '点击编辑...',
  className = '',
  displayClassName = '',
  editClassName = '',
  multiline = false,
  maxLength,
  disabled = false,
  validateLength,
  lengthMessage = '长度超限',
  getDisplayLength,
  maxDisplayLength = 18
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // 开始编辑
  const handleStartEdit = () => {
    if (disabled) return;
    setIsEditing(true);
    setEditValue(value);
  };

  // 取消编辑
  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(value);
  };

  // 保存编辑
  const handleSave = async () => {
    const trimmedValue = editValue.trim();

    // 如果值没有改变，直接退出编辑模式
    if (trimmedValue === value.trim()) {
      setIsEditing(false);
      return;
    }

    // 仅提示长度验证，不阻止保存
    // if (validateLength && !validateLength(trimmedValue)) {
    //   console.warn('长度验证失败:', lengthMessage);
    //   return; // 长度验证失败，保持编辑状态
    // }

    try {
      setIsSaving(true);
      await onSave(trimmedValue);
      setIsEditing(false);
    } catch (error) {
      console.error('保存失败:', error);
      // 保存失败时保持编辑状态，让用户有机会重试
    } finally {
      setIsSaving(false);
    }
  };

  // 处理键盘事件
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (!multiline || e.ctrlKey) {
        e.preventDefault();
        handleSave();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  // 处理失去焦点
  const handleBlur = () => {
    // 延迟执行，避免与点击取消/保存按钮冲突
    setTimeout(() => {
      if (isEditing) {
        handleSave();
      }
    }, 150);
  };

  // 自动聚焦
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // 编辑模式
  if (isEditing) {
    const isInvalid = validateLength && !validateLength(editValue);
    const baseEditClasses = `
      inline-edit border-2 ${isInvalid ? 'border-red-500' : 'border-blue-500'} rounded px-2 py-1
      focus:outline-none ${isInvalid ? 'focus:border-red-600' : 'focus:border-blue-600'} resize-none
      ${isSaving ? 'opacity-50 cursor-wait' : ''}
      ${isInvalid ? 'text-red-600' : ''}
      ${editClassName}
    `;

    if (multiline) {
      return (
        <div className={`relative ${className}`}>
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            placeholder={placeholder}
            className={baseEditClasses}
            maxLength={maxLength}
            rows={3}
            disabled={isSaving}
          />
          {isSaving && (
            <div className="absolute right-1 top-1">
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={`relative ${className}`}>
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          placeholder={placeholder}
          className={baseEditClasses}
          maxLength={maxLength}
          disabled={isSaving}
        />
        {/* 长度提示 - 仅在有显示长度函数时显示 */}
        {getDisplayLength && (
          <div className="flex justify-between items-center mt-1">
            <div className={`text-xs ${
              isInvalid ? 'text-red-500' : 'text-gray-400'
            }`}>
              当前长度: {getDisplayLength(editValue).toFixed(1)}/{maxDisplayLength}
            </div>
            {isInvalid && (
              <div className="text-xs text-red-500">
                标题过长
              </div>
            )}
          </div>
        )}
        {isSaving && (
          <div className="absolute right-1 top-1/2 transform -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </div>
    );
  }

  // 显示模式
  const displayValue = value || placeholder;
  const isEmpty = !value;

  return (
    <div
      className={`
        cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5 transition-colors
        ${isEmpty ? 'text-gray-400 italic' : ''}
        ${disabled ? 'cursor-not-allowed opacity-50' : ''}
        ${displayClassName}
        ${className}
      `}
      onClick={handleStartEdit}
    >
      {multiline ? (
        <div className="whitespace-pre-wrap break-words">
          {displayValue}
        </div>
      ) : (
        <span className={displayClassName?.includes('truncate') ? 'block truncate' : 'break-words'}>
          {displayValue}
        </span>
      )}
    </div>
  );
};